import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  DEV_NIGHT_PALETTE_STORAGE_KEY,
  parseDevNightPaletteId,
  type DevNightPaletteId,
} from '../theme/devNightPalettes';

export const DEV_NIGHT_MODE_STORAGE_KEY = 'rintintin:dev:nightMode';

function readStoredEnabled(): boolean {
  try {
    return localStorage.getItem(DEV_NIGHT_MODE_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function readStoredPalette(): DevNightPaletteId {
  try {
    return parseDevNightPaletteId(localStorage.getItem(DEV_NIGHT_PALETTE_STORAGE_KEY));
  } catch {
    return 'default';
  }
}

type DevNightModeContextValue = {
  isDevNightModeAllowed: boolean;
  devNightModeEnabled: boolean;
  setDevNightModeEnabled: (value: boolean) => void;
  devNightPalette: DevNightPaletteId;
  setDevNightPalette: (id: DevNightPaletteId) => void;
};

const DevNightModeContext = createContext<DevNightModeContextValue | null>(null);

/**
 * État partagé mode nuit + palette (une seule source de vérité pour header, paramètres, etc.).
 */
export function DevNightModeProvider({ children }: { children: ReactNode }) {
  const [devNightModeEnabled, setDevNightModeEnabledState] = useState(false);
  const [devNightPalette, setDevNightPaletteState] = useState<DevNightPaletteId>('default');

  useLayoutEffect(() => {
    setDevNightModeEnabledState(readStoredEnabled());
    setDevNightPaletteState(readStoredPalette());
  }, []);

  useEffect(() => {
    if (devNightModeEnabled) {
      document.documentElement.setAttribute('data-dev-night-mode', 'on');
      document.documentElement.setAttribute('data-night-palette', devNightPalette);
    } else {
      document.documentElement.removeAttribute('data-dev-night-mode');
      document.documentElement.removeAttribute('data-night-palette');
    }
    try {
      localStorage.setItem(DEV_NIGHT_MODE_STORAGE_KEY, devNightModeEnabled ? '1' : '0');
      localStorage.setItem(DEV_NIGHT_PALETTE_STORAGE_KEY, devNightPalette);
    } catch {
      /* ignore */
    }
  }, [devNightModeEnabled, devNightPalette]);

  const setDevNightModeEnabled = useCallback((value: boolean) => {
    setDevNightModeEnabledState(value);
  }, []);

  const setDevNightPalette = useCallback((id: DevNightPaletteId) => {
    setDevNightPaletteState(id);
  }, []);

  const value = useMemo<DevNightModeContextValue>(
    () => ({
      isDevNightModeAllowed: true,
      devNightModeEnabled,
      setDevNightModeEnabled,
      devNightPalette,
      setDevNightPalette,
    }),
    [devNightModeEnabled, setDevNightModeEnabled, devNightPalette, setDevNightPalette]
  );

  return React.createElement(DevNightModeContext.Provider, { value }, children);
}

/**
 * Toggle « mode nuit » : persistance locale + `data-dev-night-mode="on"` sur `<html>`.
 * Palette : `data-night-palette` + `DEV_NIGHT_PALETTE_STORAGE_KEY` (appliquée quand le mode nuit est ON).
 */
export function useDevNightMode(): DevNightModeContextValue {
  const ctx = useContext(DevNightModeContext);
  if (!ctx) {
    throw new Error('useDevNightMode doit être utilisé dans DevNightModeProvider');
  }
  return ctx;
}
